// ============================================
// CONTACTOSALCANCE.JS — Muestra/edita roles de contacto para un alcance dado
// Reutilizado por las 4 fichas (grupo, razón social, zona, sucursal)
// ============================================

import React, { useEffect, useState } from 'react';
import { authHeaders } from '../utils/storage';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_FONDO,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
  TIPOS_ROL, ESTADOS_ROL, AREAS_TOMADOR,
} from './helpersClientes';
import DetectorDuplicados from './DetectorDuplicados';

export default function ContactosAlcance({
  alcance_tipo, alcance_id,
  titulo = 'Contactos',
  tiposPermitidos = ['administrador', 'gerente', 'encargado_sucursal'],
}) {
  const [contactos, setContactos] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [modal, setModal] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/contactos-por-alcance?alcance_tipo=${alcance_tipo}&alcance_id=${alcance_id}`, { headers: authHeaders() }).then(x => x.json());
      setContactos((r.contactos || []).filter(c => tiposPermitidos.includes(c.tipo)));
    } finally {
      setCargando(false);
    }
  };

  const cargarPersonas = async () => {
    const r = await fetch(`${API_BASE_CLI}/api/clientes/personas`, { headers: authHeaders() }).then(x => x.json());
    setPersonas(r.personas || []);
  };

  useEffect(() => { cargar(); }, [alcance_tipo, alcance_id]);

  const quitar = async (rolId) => {
    if (!window.confirm('¿Quitar este contacto?')) return;
    await fetch(`${API_BASE_CLI}/api/clientes/roles-contacto/${rolId}`, { method: 'DELETE', headers: { ...authHeaders() } });
    cargar();
  };

  return (
    <div style={estiloTarjeta}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>{titulo}</div>
        <button style={estiloBotonPrimario} onClick={() => { cargarPersonas(); setModal(true); }}>+ Agregar</button>
      </div>

      {cargando && <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Cargando…</div>}

      {!cargando && contactos.length === 0 && (
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, padding: '8px 0' }}>
          Sin contactos asignados a este nivel.
        </div>
      )}

      <div style={{ display: 'grid', gap: 6 }}>
        {contactos.map(c => {
          const tipo = TIPOS_ROL[c.tipo] || { label: c.tipo, color: '#999' };
          const est = ESTADOS_ROL[c.estado_rol] || ESTADOS_ROL.activo_confirmado;
          return (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: COLOR_FONDO, borderRadius: 8,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>{c.nombre_completo}</div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                  <span style={{ background: tipo.color + '20', color: tipo.color, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                    {tipo.label}
                  </span>
                  {c.area && <span style={{ marginLeft: 8 }}>· {AREAS_TOMADOR.find(a => a.id === c.area)?.label || c.area}</span>}
                  {c.puesto && <span style={{ marginLeft: 8 }}>· {c.puesto}</span>}
                </div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                  {c.celular_corporativo || c.celular || ''}
                  {(c.email_corporativo || c.email) && ` · ${c.email_corporativo || c.email}`}
                </div>
              </div>
              <span
                title={est.label}
                style={{ width: 8, height: 8, borderRadius: '50%', background: est.color }}
              />
              <button
                onClick={() => quitar(c.id)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLOR_GRIS_CLARO, fontSize: 14 }}
                title="Quitar"
              >✕</button>
            </div>
          );
        })}
      </div>

      {modal && (
        <ModalAgregarContacto
          alcance_tipo={alcance_tipo}
          alcance_id={alcance_id}
          tiposPermitidos={tiposPermitidos}
          personas={personas}
          onCerrar={() => setModal(false)}
          onGuardado={() => { setModal(false); cargar(); }}
        />
      )}
    </div>
  );
}

function ModalAgregarContacto({ alcance_tipo, alcance_id, tiposPermitidos, personas, onCerrar, onGuardado }) {
  const [modo, setModo] = useState('existente'); // 'existente' | 'nueva'
  const [personaId, setPersonaId] = useState('');
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [tipo, setTipo] = useState(tiposPermitidos[0]);
  const [area, setArea] = useState('');
  const [puesto, setPuesto] = useState('');
  const [emailCorp, setEmailCorp] = useState('');
  const [celularCorp, setCelularCorp] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [similares, setSimilares] = useState(0);

  // Al elegir un similar, pasamos a modo "existente" con esa persona preseleccionada
  const usarExistente = (item) => {
    setModo('existente');
    setPersonaId(String(item.id));
    setSimilares(0);
  };

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      let pId = personaId;
      if (modo === 'nueva') {
        if (!nombreNuevo.trim()) throw new Error('Nombre requerido');
        const pr = await fetch(`${API_BASE_CLI}/api/clientes/personas`, {
          method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ nombre_completo: nombreNuevo.trim() }),
        }).then(x => x.json());
        if (!pr.ok) throw new Error(pr.error || 'Error creando persona');
        pId = pr.persona.id;
      }
      if (!pId) throw new Error('Elige o crea una persona');
      const resp = await fetch(`${API_BASE_CLI}/api/clientes/roles-contacto`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona_id: pId, tipo, area: tipo === 'administrador' ? area || null : null,
          puesto: puesto || null, email_corporativo: emailCorp || null,
          celular_corporativo: celularCorp || null,
          alcance_tipo, alcance_id,
        }),
      });
      const r = await resp.json();
      if (resp.status === 409) {
        alert(`⚠️ ${r.error}`);
        return;
      }
      if (!r.ok) throw new Error(r.error || 'Error creando rol');
      onGuardado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 480,
        fontFamily: PP,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 16 }}>Agregar contacto</div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button
            onClick={() => setModo('existente')}
            style={{
              ...estiloBotonSecundario,
              background: modo === 'existente' ? COLOR_PRINCIPAL : '#fff',
              color: modo === 'existente' ? '#fff' : COLOR_GRIS,
              border: 'none',
            }}
          >Persona existente</button>
          <button
            onClick={() => setModo('nueva')}
            style={{
              ...estiloBotonSecundario,
              background: modo === 'nueva' ? COLOR_PRINCIPAL : '#fff',
              color: modo === 'nueva' ? '#fff' : COLOR_GRIS,
              border: 'none',
            }}
          >Crear nueva</button>
        </div>

        {modo === 'existente' ? (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Persona *</label>
            <select style={estiloInput} value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {personas.map(p => <option key={p.id} value={p.id}>{p.nombre_completo}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre completo *</label>
            <input style={estiloInput} value={nombreNuevo} onChange={(e) => setNombreNuevo(e.target.value)} placeholder="Ej: Juan Pérez" />
            <DetectorDuplicados
              tipo="persona"
              nombreActual={nombreNuevo}
              onSeleccionarExistente={usarExistente}
              onCambioResultados={setSimilares}
            />
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Tipo de rol *</label>
          <select style={estiloInput} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {tiposPermitidos.map(t => <option key={t} value={t}>{TIPOS_ROL[t]?.label || t}</option>)}
          </select>
        </div>

        {tipo === 'administrador' && (
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Área</label>
            <select style={estiloInput} value={area} onChange={(e) => setArea(e.target.value)}>
              <option value="">— Sin especificar —</option>
              {AREAS_TOMADOR.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Puesto (opcional)</label>
          <input style={estiloInput} value={puesto} onChange={(e) => setPuesto(e.target.value)} placeholder="Ej: Gerente de operaciones" />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Celular corporativo</label>
            <input style={estiloInput} value={celularCorp} onChange={(e) => setCelularCorp(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Email corporativo</label>
            <input style={estiloInput} value={emailCorp} onChange={(e) => setEmailCorp(e.target.value)} />
          </div>
        </div>

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={guardar} disabled={guardando}>
            {guardando
              ? 'Guardando…'
              : (modo === 'nueva' && similares > 0 ? 'Sí, crear nueva persona y asignar' : 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  );
}
